import React from "react";
import { Calendar, Clock, MapPin, Video } from "lucide-react";

function toJsDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function formatDate(date, fallbackLabel) {
  if (fallbackLabel) return fallbackLabel;
  const d = toJsDate(date);
  if (!d) return "Data e papërcaktuar";
  return d.toLocaleDateString("sq-AL", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(timeString) {
  if (!timeString) return null;
  try {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return timeString;
  }
}

function statusLabel(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "Në pritje";
  if (s === "confirmed") return "Konfirmuar";
  if (s === "completed") return "Përfunduar";
  if (s === "cancelled" || s === "canceled") return "Anuluar";
  return "Status i panjohur";
}

function statusClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "confirmed") return "p2-apt-mini-status p2-apt-mini-status--confirmed";
  if (s === "pending") return "p2-apt-mini-status p2-apt-mini-status--pending";
  if (s === "completed") return "p2-apt-mini-status p2-apt-mini-status--completed";
  if (s === "cancelled" || s === "canceled")
    return "p2-apt-mini-status p2-apt-mini-status--cancelled";
  return "p2-apt-mini-status";
}

export default function AppointmentMiniCard({ appointment }) {
  if (!appointment) return null;

  const doctorName =
    appointment.doctorName ||
    appointment.doctorInfo?.name ||
    "Mjek pa emër";

  const specialty =
    appointment.specialty ||
    appointment.doctorInfo?.specialty ||
    "Specialitet i papërcaktuar";

  const rawDate = appointment.scheduledDate || appointment.date;
  const dateLabel = formatDate(rawDate, appointment.dateLabel);
  const timeLabel = formatTime(appointment.scheduledTime || appointment.time);

  const type =
    appointment.appointmentType ||
    appointment.type ||
    (appointment.isOnline ? "online" : "in-person");

  const isOnline =
    appointment.isOnline ||
    (typeof type === "string" && type.toLowerCase().includes("online"));

  const clinicLabel =
    appointment.clinicName ||
    appointment.doctorInfo?.clinicName ||
    appointment.locationLabel ||
    "";

  const serviceLabel = appointment.serviceName || appointment.service || "";
  const priceLabel =
    appointment.servicePriceLabel ||
    (appointment.servicePrice ? `${appointment.servicePrice} Lek` : "");

  return (
    <div className="p2-apt-mini">
      {/* Left icon */}
      <div className="p2-apt-mini-icon">
        <Calendar className="p2-apt-mini-icon-svg" />
      </div>

      {/* Main text */}
      <div className="p2-apt-mini-main">
        {/* Top: doctor + specialty */}
        <p className="p2-apt-mini-title">
          {doctorName ? `Dr. ${doctorName}` : "Takim mjekësor"}
          {specialty ? ` • ${specialty}` : ""}
        </p>

        {/* Middle: date + time + type */}
        <div className="p2-apt-mini-meta-row">
          <span className="p2-apt-mini-meta">
            {dateLabel}
            {timeLabel ? ` • ${timeLabel}` : ""}
          </span>

          <span className="p2-apt-mini-meta">
            {isOnline ? (
              <>
                <Video className="p2-apt-mini-meta-icon" />
                Vizitë online
              </>
            ) : (
              <>
                <MapPin className="p2-apt-mini-meta-icon" />
                Vizitë në klinikë
              </>
            )}
          </span>
        </div>

        {/* Bottom: clinic / service / price */}
        {(clinicLabel || serviceLabel || priceLabel) && (
          <div className="p2-apt-mini-meta-row p2-apt-mini-meta-row--secondary">
            {clinicLabel && (
              <span className="p2-apt-mini-meta p2-apt-mini-meta--clinic">
                <MapPin className="p2-apt-mini-meta-icon" />
                {clinicLabel}
              </span>
            )}
            {serviceLabel && (
              <span className="p2-apt-mini-meta">
                {serviceLabel}
              </span>
            )}
            {priceLabel && (
              <span className="p2-apt-mini-meta p2-apt-mini-meta--price">
                {priceLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Status pill */}
      {appointment.status && (
        <span className={statusClass(appointment.status)}>
          {statusLabel(appointment.status)}
        </span>
      )}
    </div>
  );
}
